/**
 * BNZ Collection Switcher Component
 * Handles switching between different collection sliders
 */

if (!customElements.get('bnz-collection-switcher')) {
  class BnzCollectionSwitcher extends HTMLElement {
    constructor() {
      super();
      this.tabs = this.querySelectorAll('.bnz-collection-switcher__tab');
      this.panels = this.querySelectorAll('.bnz-collection-switcher__panel');
      this.currentIndex = 0;
      this.indicator = this.querySelector('[data-tabs-indicator]');
      this.indicatorCurrent = this.querySelector('[data-current-index]');
      this.progressFill = this.querySelector('[data-progress-fill]');

      this.init();
    }

    init() {
      if (this.tabs.length === 0 || this.panels.length === 0) {
        console.warn('BNZ Collection Switcher: No tabs or panels found');
        return;
      }

      // Set up event listeners for tabs
      this.tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => this.switchToCollection(index));
        
        // Add keyboard navigation
        tab.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this.switchToCollection(index);
          } else if (e.key === 'ArrowLeft') {
            e.preventDefault();
            this.navigateTabs('prev');
          } else if (e.key === 'ArrowRight') {
            e.preventDefault();
            this.navigateTabs('next');
          }
        });
      });

      // Initialize first collection carousel if using slider style
      this.initializeActiveCarousel();

      // Initialize indicator + progressbar
      this.updateIndicator();
      this.updateProgress();

      // Retirer la classe de chargement une fois que tout est initialisé
      // Utiliser requestAnimationFrame pour s'assurer que les styles sont appliqués
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.classList.remove('bnz-collection-switcher--loading');
        });
      });
    }

    switchToCollection(index) {
      if (index === this.currentIndex || index < 0 || index >= this.panels.length) {
        return;
      }

      // Update tabs
      this.tabs.forEach((tab, i) => {
        if (i === index) {
          tab.classList.add('is-active');
          tab.setAttribute('aria-selected', 'true');
          tab.setAttribute('tabindex', '0');
        } else {
          tab.classList.remove('is-active');
          tab.setAttribute('aria-selected', 'false');
          tab.setAttribute('tabindex', '-1');
        }
      });

      // Update panels - use visibility instead of display for lazysizes compatibility
      this.panels.forEach((panel, i) => {
        if (i === index) {
          panel.classList.add('is-active');
          panel.setAttribute('aria-hidden', 'false');
          panel.style.display = 'block';
          panel.style.visibility = 'visible';
        } else {
          panel.classList.remove('is-active');
          panel.setAttribute('aria-hidden', 'true');
          panel.style.display = 'none';
          panel.style.visibility = 'hidden';
        }
      });

      this.currentIndex = index;

      // Update indicator + progressbar if they exist
      this.updateIndicator();
      this.updateProgress();

      // Load images for the newly active panel
      this.loadImagesForActivePanel();

      // Initialize carousel for the newly active panel
      this.initializeActiveCarousel();

      // Preload adjacent collections after a delay
      this.preloadAdjacentCollections();

      // Trigger custom event for analytics or other purposes
      this.dispatchEvent(new CustomEvent('collection-switched', {
        detail: { index, tab: this.tabs[index], panel: this.panels[index] },
        bubbles: true
      }));
    }

    navigateTabs(direction) {
      let newIndex;
      
      if (direction === 'next') {
        newIndex = (this.currentIndex + 1) % this.tabs.length;
      } else {
        newIndex = (this.currentIndex - 1 + this.tabs.length) % this.tabs.length;
      }

      this.tabs[newIndex].focus();
      this.switchToCollection(newIndex);
    }

    loadImagesForActivePanel() {
      const activePanel = this.panels[this.currentIndex];
      if (!activePanel) return;

      // Force lazysizes to detect and load images in the active panel
      if (window.lazySizes) {
        // Use requestAnimationFrame to ensure DOM is updated
        requestAnimationFrame(() => {
          // Find all lazy images in the active panel
          const lazyImages = activePanel.querySelectorAll('.lazyload:not(.lazyloaded)');
          
          // Trigger lazysizes to load these images
          lazyImages.forEach(img => {
            if (img.dataset.src || img.dataset.srcset) {
              // Use the correct lazysizes API
              if (lazySizes.loader && typeof lazySizes.loader.unveil === 'function') {
                lazySizes.loader.unveil(img);
              } else {
                // Fallback: trigger lazySizes manually
                img.classList.add('lazyload');
                if (img.dataset.src) {
                  img.src = img.dataset.src;
                }
                if (img.dataset.srcset) {
                  img.srcset = img.dataset.srcset;
                }
                if (img.dataset.sizes) {
                  img.sizes = img.dataset.sizes;
                }
                img.classList.remove('lazyload');
                img.classList.add('lazyloaded');
              }
            }
          });

          // Also trigger lazySizes.init() to scan for new images
          if (typeof lazySizes.init === 'function') {
            lazySizes.init();
          }
        });
      }

      // Load hover images for products in the active panel
      this.loadHoverImages(activePanel);
    }

    loadHoverImages(panel) {
      // Find all product cards in the panel that have hover images
      const productCards = panel.querySelectorAll('product-card');
      
      productCards.forEach(card => {
        const media = card.querySelector('[data-product-media]');
        if (!media || !media.classList.contains('has-hover')) return;

        // Check if secondary image exists but is not loaded
        const secondaryImage = media.querySelector('[data-secondary-image]');
        if (secondaryImage && !secondaryImage.classList.contains('lazyloaded')) {
          // Load the secondary image lazily
          if (window.lazySizes) {
            if (lazySizes.loader && typeof lazySizes.loader.unveil === 'function') {
              lazySizes.loader.unveil(secondaryImage);
            } else {
              // Fallback: load image manually
              if (secondaryImage.dataset.src) {
                secondaryImage.src = secondaryImage.dataset.src;
              }
              if (secondaryImage.dataset.srcset) {
                secondaryImage.srcset = secondaryImage.dataset.srcset;
              }
              if (secondaryImage.dataset.sizes) {
                secondaryImage.sizes = secondaryImage.dataset.sizes;
              }
              secondaryImage.classList.remove('lazyload');
              secondaryImage.classList.add('lazyloaded');
            }
          }
        }
      });
    }

    preloadAdjacentCollections() {
      // Preload images from adjacent collections after a delay
      setTimeout(() => {
        const nextIndex = (this.currentIndex + 1) % this.panels.length;
        const prevIndex = (this.currentIndex - 1 + this.panels.length) % this.panels.length;

        [nextIndex, prevIndex].forEach(index => {
          const panel = this.panels[index];
          if (!panel) return;

          // Only preload first few images to avoid excessive bandwidth
          const firstImages = panel.querySelectorAll('.lazyload:not(.lazyloaded)');
          const imagesToPreload = Array.from(firstImages).slice(0, 4); // Preload first 4 images

          imagesToPreload.forEach(img => {
            if (window.lazySizes && (img.dataset.src || img.dataset.srcset)) {
              // Use low priority preload
              const link = document.createElement('link');
              link.rel = 'preload';
              link.as = 'image';
              if (img.dataset.srcset) {
                link.imagesrcset = img.dataset.srcset;
                link.imagesizes = img.dataset.sizes || 'auto';
              } else if (img.dataset.src) {
                link.href = img.dataset.src;
              }
              link.fetchPriority = 'low';
              document.head.appendChild(link);
            }
          });
        });
      }, 500); // Wait 500ms before preloading
    }

    initializeActiveCarousel() {
      // Find the active panel's carousel component
      const activePanel = this.panels[this.currentIndex];
      if (!activePanel) return;

      const carousel = activePanel.querySelector('products-carousel');
      if (!carousel) return;

      // Check if carousel has a reinit or init method
      if (typeof carousel.init === 'function') {
        // Give the panel a moment to become visible before initializing
        setTimeout(() => {
          carousel.init();
        }, 50);
      } else if (typeof carousel.reinit === 'function') {
        setTimeout(() => {
          carousel.reinit();
        }, 50);
      }

      // If using Swiper, update it after initialization
      const swiper = carousel.querySelector('.swiper');
      if (swiper && swiper.swiper) {
        setTimeout(() => {
          swiper.swiper.update();
        }, 100);
      }
    }

    // Public method to programmatically switch collections
    goToCollection(index) {
      this.switchToCollection(index);
    }

    // Public method to get current collection index
    getCurrentIndex() {
      return this.currentIndex;
    }

    updateIndicator() {
      if (this.indicatorCurrent) {
        // Update to show 1-based index (user-friendly)
        this.indicatorCurrent.textContent = this.currentIndex + 1;
      }
    }

    updateProgress() {
      if (!this.progressFill || this.tabs.length === 0) return;
      const progress = (this.currentIndex + 1) / this.tabs.length;
      this.progressFill.style.transform = `scaleX(${progress})`;
    }
  }

  customElements.define('bnz-collection-switcher', BnzCollectionSwitcher);
}
